import { filter, pipe } from 'graphql-yoga'
import { Resolver, Root, Subscription } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { getRepository, pubsub } from '@things-factory/shell'

import { DataOoc } from './data-ooc.js'

@Resolver(DataOoc)
export class DataOocSubscription {
  /**
   * Real-time subscription for out-of-control (OOC) events.
   * This subscription allows users with supervisory roles to receive immediate notifications
   * when new OOC events are created or updated in their domain.
   */
  @Subscription({
    description:
      'Real-time subscription for out-of-control (OOC) events. Users with supervisory roles receive immediate notifications when OOC events occur in their domain.',
    subscribe: ({ args, context, info }) => {
      const { domain, user } = context.state
      const subdomain = domain?.subdomain

      // Validate domain access
      if (!domain) {
        throw new Error('domain required')
      }

      // Check user domain access permissions
      if (!user.domains?.find(d => d.subdomain === subdomain) && !process.superUserGranted(domain, user)) {
        throw new Error(`domain(${subdomain}) is not working for user(${user.email}).`)
      }

      return pipe(
        pubsub.subscribe('data-ooc'),
        filter(async (payload: { dataOoc: DataOoc; supervisoryRoleId: string }) => {
          const { dataOoc, supervisoryRoleId } = payload
          const { domain } = dataOoc

          // Filter by domain to ensure users only receive events from their domain
          if (subdomain !== domain?.subdomain) {
            return false
          }

          // Check if the user has the supervisory role required for this OOC event
          const userWithRoles: User = await getRepository(User).findOne({
            where: { id: user.id },
            relations: ['roles']
          })

          return !!userWithRoles.roles.find(role => role.id === supervisoryRoleId)
        })
      )
    }
  })
  dataOoc(@Root() payload: { dataOoc: DataOoc; supervisoryRoleId: string }): DataOoc {
    /**
     * Returns the OOC event data from the subscription payload.
     * This resolver extracts the DataOoc object from the subscription payload
     * and provides it to the client for real-time updates.
     */
    return payload.dataOoc
  }
}
